// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getMonthlyDeals, MonthlyDeal } from "@bg/App/Services/MonthlyDealsService";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import {
    ApiHandler,
    createHandlerChainRunner,
    executeAwaitableHandlerChain,
} from "@insite/client-framework/HandlerCreator";
import { getProductById } from "@insite/client-framework/Services/ProductServiceV2";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { RealTimePricingModel } from "@insite/client-framework/Types/ApiModels";

type HandlerType = ApiHandler<{}, MonthlyDeal[]>;

export const DispatchBeginLoadMonthlyDeals: HandlerType = (props: any) => {
    props.dispatch({
        type: "xMonthlyDeals/BeginLoadMonthlyDeals",
    });
};

export const RequestDataFromApi: HandlerType = async props => {
    const response = await getMonthlyDeals();
    if (response.status === 400) {
        return;
    }
    props.apiResult = response;
};

export const LoadProductsInfo: HandlerType = async props => {
    if (!props.apiResult) {
        return;
    }

    const promise = props.apiResult.map(async d => {
        const product = await getProductById({ id: d.productId });
        return product;
    });
    const products = await Promise.all(promise);
    props.apiResult = props.apiResult.map(deal => {
        const productData = products.find(d => d.id === deal.productId);
        const product: ProductContextModel = {
            product: productData!,
            productInfo: {
                productId: productData ? productData.id : "",
                productDetailPath: productData ? productData.canonicalUrl : "",
                qtyOrdered: 1,
                unitOfMeasure: deal.unitOfMeasure,
            },
        };
        return {
            ...deal,
            product,
            pageUrl: productData?.canonicalUrl,
        };
    });
};

export const LoadRealTimePrices: HandlerType = async props => {
    if (!props.apiResult || props.apiResult.length === 0) {
        return;
    }

    const { dispatch, getState } = props;

    const realTimePrices = await executeAwaitableHandlerChain<
        Parameters<typeof loadRealTimePricing>[0],
        RealTimePricingModel
    >(
        loadRealTimePricing,
        {
            productPriceParameters: props.apiResult.map(({ unitOfMeasure, productId }) => ({
                unitOfMeasure,
                productId,
                qtyOrdered: 1,
            })),
        },
        { dispatch, getState },
    );

    realTimePrices.realTimePricingResults?.forEach(price => {
        const productIndex = props.apiResult.findIndex(p => p.productId === price.productId);
        if (props.apiResult[productIndex].product) {
            props.apiResult[productIndex].product!.productInfo.pricing = price;
        }
    });
};

export const DispatchCompleteLoadMonthlyDeals: HandlerType = (props: any) => {
    props.dispatch({
        type: "xMonthlyDeals/CompleteLoadMonthlyDeals",
        monthlyDeals: props.apiResult,
    });
};

export const chain = [
    DispatchBeginLoadMonthlyDeals,
    RequestDataFromApi,
    LoadProductsInfo,
    LoadRealTimePrices,
    DispatchCompleteLoadMonthlyDeals,
];

const loadMonthlyDeals = createHandlerChainRunner(chain, "loadMonthlyDeals");
export default loadMonthlyDeals;
